import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../api';

export default function TimesheetDetail() {
  const {id} = useParams();
  const [timesheet, setTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [correctionEntry, setCorrectionEntry] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({requestedClockInTime: '', requestedClockOutTime: '', reason: ''});
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionSuccess, setCorrectionSuccess] = useState(false);

  const load = () => {
    Promise.all([
      api(`/timesheets/timesheets/${id}`).then(r => r.ok ? r.json() : null),
      api(`/timesheets/timesheets/${id}/entries`).then(r => r.ok ? r.json() : []),
      api('/timesheets/time-corrections').then(r => r.ok ? r.json() : []),
    ]).then(([ts, ents, allCorrections]) => {
      setTimesheet(ts);
      const sorted = ents.sort((a, b) => b.date.localeCompare(a.date));
      setEntries(sorted);
      const entryIds = new Set(sorted.map(e => e.id));
      setCorrections(allCorrections.filter(c => entryIds.has(c.timesheetEntryId)));
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <Layout><div className="p-6">Loading...</div></Layout>;
  if (!timesheet) return <Layout><div className="p-6">Timesheet not found.</div></Layout>;

  const statusBadge = timesheet.status === 'APPROVED'
    ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">APPROVED</span>
    : <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">DRAFT</span>;

  const fmtClockTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const toLocalDatetimeStr = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const openCorrection = (entry) => {
    setCorrectionEntry(entry);
    setCorrectionForm({
      requestedClockInTime: toLocalDatetimeStr(entry.startAt),
      requestedClockOutTime: toLocalDatetimeStr(entry.endAt),
      reason: '',
    });
    setCorrectionSuccess(false);
  };

  const handleCorrectionSubmit = async (e) => {
    e.preventDefault();
    setCorrectionSubmitting(true);
    const body = { timesheetEntryId: correctionEntry.id, reason: correctionForm.reason };
    if (correctionForm.requestedClockInTime) body.requestedClockInTime = new Date(correctionForm.requestedClockInTime).toISOString();
    if (correctionForm.requestedClockOutTime) body.requestedClockOutTime = new Date(correctionForm.requestedClockOutTime).toISOString();
    const res = await api('/timesheets/time-corrections', { method: 'POST', body: JSON.stringify(body) });
    setCorrectionSubmitting(false);
    if (res.ok) {
      setCorrectionSuccess(true);
      load();
      setTimeout(() => setCorrectionEntry(null), 1500);
    }
  };

  return (
    <Layout>
      <Link to="/timesheets" className="text-amber-600 hover:text-amber-800 mb-4 inline-block">&larr; Back</Link>
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{timesheet.employeeName}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Period</label>
            <p className="mt-1 text-gray-900">{timesheet.startDate} — {timesheet.endDate}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <p className="mt-1">{statusBadge}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Total Hours</label>
            <p className="mt-1 text-gray-900 font-semibold">{timesheet.totalHours != null ? Number(timesheet.totalHours).toFixed(2) : '—'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">Entries ({entries.length})</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Description</th>
              <th className="px-4 sm:px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {entries.map(e => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="px-4 sm:px-6 py-4 text-gray-900">{e.date}</td>
                <td className="px-4 sm:px-6 py-4 text-gray-600">{fmtClockTime(e.startAt)}</td>
                <td className="px-4 sm:px-6 py-4 text-gray-600">{fmtClockTime(e.endAt)}</td>
                <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">{e.totalHours != null ? Number(e.totalHours).toFixed(2) : '—'}</td>
                <td className="px-4 sm:px-6 py-4 text-gray-600 hidden sm:table-cell">{e.description || '—'}</td>
                <td className="px-4 sm:px-6 py-4">
                  {e.entrySource === 'CLOCK' && <button onClick={() => openCorrection(e)} className="text-amber-600 hover:text-amber-800 text-sm">Request Correction</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length === 0 && <div className="text-center py-12 text-gray-500">No entries yet.</div>}
      </div>

      {corrections.length > 0 && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-6 border-b">
            <h3 className="text-xl font-bold text-gray-900">Correction Requests</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {corrections.map(c => (
              <div key={c.id} className="px-4 sm:px-6 py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-900 font-medium">{c.entryDate}</p>
                    <p className="text-sm text-gray-500 mt-1">Requested: {fmtClockTime(c.requestedClockInTime)} → {fmtClockTime(c.requestedClockOutTime)}</p>
                    <p className="text-sm text-gray-400 mt-1 italic">"{c.reason}"</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    c.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    c.status === 'DENIED' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {correctionEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Request Time Correction</h3>
            <p className="text-sm text-gray-500 mb-4">Entry: {correctionEntry.date} — {fmtClockTime(correctionEntry.startAt)} to {fmtClockTime(correctionEntry.endAt)}</p>
            {correctionSuccess ? (
              <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">Correction request submitted!</div>
            ) : (
              <form onSubmit={handleCorrectionSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Corrected Clock In</label>
                  <input type="datetime-local" value={correctionForm.requestedClockInTime}
                    onChange={e => setCorrectionForm(f => ({...f, requestedClockInTime: e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Corrected Clock Out</label>
                  <input type="datetime-local" value={correctionForm.requestedClockOutTime}
                    onChange={e => setCorrectionForm(f => ({...f, requestedClockOutTime: e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea required value={correctionForm.reason}
                    onChange={e => setCorrectionForm(f => ({...f, reason: e.target.value}))}
                    rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setCorrectionEntry(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={correctionSubmitting}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                    {correctionSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
